import { useEffect, useMemo, useState, type FormEvent } from "react";
import { BookUser, BriefcaseBusiness, Building2, ChevronDown, Filter, Pencil, Phone, Plus, Search, Trash2, Users } from "lucide-react";
import { api, ApiError, query } from "../api";
import { PersonnelProfileModal } from "./PersonnelProfile";
import type { ApiList, Balance, OrganizationUnit, Team, User } from "../types";
import { initials, numberLabel, roleLabel, skillLabel, SKILL_OPTIONS } from "../utils";
import {
  Alert,
  Button,
  Empty,
  Field,
  Loading,
  Modal,
  PageHeader,
  SelectField,
} from "../components/ui";

type DirectoryUser = User & { balances?: Balance; canViewProfile?: boolean };
interface DirectoryContact {
  id: string | number;
  name: string;
  phone: string;
  createdBy?: { id: string | number; displayName: string } | null;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
}
const unitLabels = {
  COMPOSITE: "Kompozit",
  STRUCTURAL: "Yapısal",
  PAINT: "Boya",
  R1_R2: "R1-R2",
} as const;
const unitOrder: OrganizationUnit[] = ["COMPOSITE", "STRUCTURAL", "PAINT", "R1_R2"];

function DirectoryPersonCard({ person, onProfile, onPublicProfile }: { person: DirectoryUser; onProfile: () => void; onPublicProfile: () => void }) {
  const mainTeam = person.mainTeam ?? person.assignment?.mainTeam;
  const pattern = person.schedulePattern ?? person.assignment?.schedulePattern;
  return (
    <article className="person-card">
      <header>
        <div className={`avatar avatar--team-${mainTeam?.toLowerCase() ?? "none"}`}>{initials(person.displayName)}</div>
        <div>
          <strong>{person.displayName}</strong>
          <span>{roleLabel(person.role)}</span>
          <span className={`person-card__unit person-card__unit--${person.organizationUnit?.toLowerCase() ?? "none"}`}>
            <Building2 aria-hidden="true" />{person.organizationUnit ? unitLabels[person.organizationUnit] : "Birimsiz"}
          </span>
        </div>
        <span className={`team-dot team-dot--${mainTeam?.toLowerCase() ?? "none"}`}>{mainTeam ?? "—"}</span>
      </header>
      <dl>
        <div><dt>Sicil numarası</dt><dd>{person.loginCode}</dd></div>
        <div><dt><BriefcaseBusiness size={15} />Ünvan</dt><dd>{person.title ?? "Belirtilmemiş"}</dd></div>
        <div><dt>Alt düzen</dt><dd>{pattern ?? "—"}</dd></div>
        <div><dt>Skill</dt><dd>{person.skills?.length ? person.skills.map(skillLabel).join(", ") : "—"}</dd></div>
        {person.balances ? <div><dt>Bakiyeler</dt><dd>Yıllık {numberLabel(person.balances.annual)} · Mazeret {numberLabel(person.balances.excuse)}</dd></div> : null}
      </dl>
      {person.phone ? <a className="phone-link" href={`tel:${person.phone}`}><Phone size={17} />{person.phone}</a> : <span className="phone-empty"><Phone size={17} />Telefon paylaşılmamış</span>}
      <Button variant="secondary" onClick={person.canViewProfile ? onProfile : onPublicProfile}>Profil detayları</Button>
      <Button variant="secondary" onClick={onPublicProfile}>İletişim ve yetkinlikler</Button>
    </article>
  );
}

export function DirectoryScreen(_props: { currentUser: User }) {
  const [profileId, setProfileId] = useState<string | number | null>(null);
  const [publicProfileId, setPublicProfileId] = useState<string | number | null>(null);
  const [items, setItems] = useState<DirectoryUser[]>([]);
  const [contacts, setContacts] = useState<DirectoryContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [unit, setUnit] = useState<OrganizationUnit | "">("");
  const [team, setTeam] = useState<Team | "">("");
  const [skill, setSkill] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(() => window.innerWidth > 640);
  const [contactOpen, setContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<DirectoryContact | null>(null);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState("");
  const [openUnits, setOpenUnits] = useState<Set<OrganizationUnit>>(() => new Set(unitOrder));
  const activeFilterCount = [unit, team, skill].filter(Boolean).length;
  const unitGroups = useMemo(() => unitOrder.map((organizationUnit) => {
    const people = items.filter((person) => person.organizationUnit === organizationUnit);
    const teams = [...new Set(people.map((person) => person.mainTeam ?? person.assignment?.mainTeam).filter(Boolean))].sort();
    return { unit: organizationUnit, people, teams };
  }).filter((group) => group.people.length), [items]);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    async function loadPeople() {
      const people: DirectoryUser[] = [];
      let offset: number | null = 0;
      while (offset !== null && active) {
        const value: ApiList<DirectoryUser> & { nextOffset?: number | null } = await api(
          `/api/personnel${query({ active: true, search: debouncedSearch, unit, team, skill, profiles: false, limit: 200, offset })}`);
        people.push(...value.items);
        offset = value.nextOffset ?? null;
      }
      return { items: people };
    }
    loadPeople().then((value) => {
      if (!active) return;
      setItems(value.items);
    }).catch((caught) => {
      if (active) setError(caught instanceof ApiError ? caught.message : "Personel rehberi yüklenemedi.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [debouncedSearch, unit, team, skill]);

  async function loadContacts() {
    try {
      const value = await api<ApiList<DirectoryContact>>(`/api/directory/contacts${query({ search: debouncedSearch })}`);
      setContacts(value.items);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Ortak numaralar yüklenemedi.");
    }
  }

  useEffect(() => { void loadContacts(); }, [debouncedSearch]);

  function openContact(contact?: DirectoryContact) {
    setEditingContact(contact ?? null);
    setContactName(contact?.name ?? "");
    setContactPhone(contact?.phone ?? "");
    setContactError("");
    setContactOpen(true);
  }

  async function saveContact(event: FormEvent) {
    event.preventDefault();
    setContactSaving(true);
    setContactError("");
    try {
      await api(editingContact ? `/api/directory/contacts/${encodeURIComponent(String(editingContact.id))}` : "/api/directory/contacts", {
        method: editingContact ? "PATCH" : "POST",
        body: { name: contactName.trim(), phone: contactPhone.trim() },
      });
      setContactOpen(false);
      await loadContacts();
    } catch (caught) {
      setContactError(caught instanceof ApiError ? caught.message : "Rehber kaydı kaydedilemedi.");
    } finally {
      setContactSaving(false);
    }
  }

  async function deleteContact(contact: DirectoryContact) {
    if (!window.confirm(`${contact.name} rehber kaydı silinsin mi?`)) return;
    try {
      await api(`/api/directory/contacts/${encodeURIComponent(String(contact.id))}`, { method: "DELETE" });
      await loadContacts();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Rehber kaydı silinemedi.");
    }
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="İLETİŞİM"
        title="Rehber"
        description="Tüm ekiplerin personel iletişim, yetki ve vize bilgileri ile ortak operasyon numaraları."
        actions={<Button onClick={() => openContact()}><Plus size={18} />Numara ekle</Button>}
      />
      <div className="directory-toolbar">
        <label className="search-field">
          <Search />
          <span className="sr-only">Rehberde ara</span>
          <input
            placeholder="Personel, sicil veya numara adı ara"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <button type="button" className="directory-filter-toggle" aria-expanded={filtersOpen} aria-controls="directory-filter-fields" onClick={() => setFiltersOpen((current) => !current)}>
          <Filter aria-hidden="true" /> Filtreler{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </button>
      </div>
      <div className="directory-filters" id="directory-filter-fields" hidden={!filtersOpen}>
        <SelectField label="Birim" value={unit} onChange={(event) => setUnit(event.target.value as OrganizationUnit | "")}>
          <option value="">Tüm birimler</option>
          {Object.entries(unitLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </SelectField>
        <SelectField
          label="Ekip"
          value={team}
          onChange={(event) => setTeam(event.target.value as Team | "")}
        >
          <option value="">Tüm ekipler</option>
          {["A", "B", "C", "D"].map((item) => (
            <option key={item}>{item}</option>
          ))}
        </SelectField>
        <SelectField
          label="Skill"
          value={skill}
          onChange={(event) => setSkill(event.target.value)}
        >
          <option value="">Tüm skill'ler</option>
          {SKILL_OPTIONS.map((item) => (
            <option value={item} key={item}>{skillLabel(item)}</option>
          ))}
        </SelectField>
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <section className="section-block directory-contacts-section">
        <div className="section-heading">
          <div><span className="eyebrow">ORTAK NUMARALAR</span><h2>Operasyon ve birim numaraları</h2></div>
          <span className="directory-result-count">{contacts.length} kayıt</span>
        </div>
        {contacts.length ? <div className="directory-contact-grid">
          {contacts.map((contact) => <article className="directory-contact-card" key={contact.id}>
            <span className="directory-contact-card__icon"><Building2 aria-hidden="true" /></span>
            <div><strong>{contact.name}</strong><small>{contact.createdBy ? `${contact.createdBy.displayName} tarafından eklendi` : "Sistem kaydı"}</small></div>
            <a className="phone-link" href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`}><Phone size={17} />{contact.phone}</a>
            {contact.canEdit ? <div className="directory-contact-card__actions">
              <button type="button" className="icon-button" onClick={() => openContact(contact)} aria-label={`${contact.name} kaydını düzenle`}><Pencil /></button>
              <button type="button" className="icon-button icon-button--danger" onClick={() => void deleteContact(contact)} aria-label={`${contact.name} kaydını sil`}><Trash2 /></button>
            </div> : null}
          </article>)}
        </div> : <Empty icon={<BookUser />} title="Ortak numara bulunamadı" text={debouncedSearch ? "Aramanızla eşleşen ortak numara yok." : "İlk ortak operasyon numarasını ekleyebilirsiniz."} />}
      </section>
      <section className="section-block directory-personnel-section">
        <div className="section-heading"><div><span className="eyebrow">PERSONEL</span><h2>Birimlere göre personel</h2></div><span className="directory-result-count">{items.length} kişi</span></div>
      {loading ? (
        <Loading label="Rehber yükleniyor" />
      ) : items.length ? (
        <div className="directory-unit-list">
          {unitGroups.map((group) => (
            <details
              className={`directory-unit-group directory-unit-group--${group.unit.toLowerCase()}`}
              open={Boolean(debouncedSearch || unit || openUnits.has(group.unit))}
              onToggle={(event) => {
                if (debouncedSearch || unit) return;
                const expanded = event.currentTarget.open;
                setOpenUnits((current) => {
                  const next = new Set(current);
                  if (expanded) next.add(group.unit); else next.delete(group.unit);
                  return next;
                });
              }}
              key={`${group.unit}-${unit || "all"}-${debouncedSearch ? "search" : "browse"}`}
            >
              <summary>
                <span className="directory-unit-group__identity"><span className="directory-unit-group__icon"><Building2 aria-hidden="true" /></span><span><strong>{unitLabels[group.unit]}</strong><small>{group.teams.length ? `${group.teams.join(", ")} ekipleri` : "Yönetim vardiyası"}</small></span></span>
                <span className="directory-unit-group__count">{group.people.length} kişi</span>
                <ChevronDown className="directory-unit-group__chevron" aria-hidden="true" />
              </summary>
              <div className="directory-grid">{group.people.map((person) => <DirectoryPersonCard person={person} onProfile={() => setProfileId(person.id)} onPublicProfile={() => setPublicProfileId(person.id)} key={person.id} />)}</div>
            </details>
          ))}
        </div>
      ) : (
        <Empty
          icon={<Users />}
          title="Personel bulunamadı"
          text="Arama ve filtrelerinize uyan aktif personel yok."
        />
      )}
      </section>
      <PersonnelProfileModal id={profileId} onClose={() => setProfileId(null)} />
      <PersonnelProfileModal id={publicProfileId} onClose={() => setPublicProfileId(null)} publicOnly />
      <Modal open={contactOpen} onClose={() => setContactOpen(false)} title={editingContact ? "Numarayı düzenle" : "Rehbere numara ekle"}>
        <form className="form-stack" onSubmit={saveContact}>
          {contactError ? <Alert tone="danger">{contactError}</Alert> : null}
          <Field label="Numara adı / ait olduğu birim" value={contactName} minLength={2} maxLength={100} onChange={(event) => setContactName(event.target.value)} placeholder="Örn. birim veya operasyon adı" required />
          <Field label="Telefon numarası" type="tel" value={contactPhone} minLength={3} maxLength={40} onChange={(event) => setContactPhone(event.target.value)} placeholder="Telefon numarasını girin" required />
          <div className="modal-actions"><Button type="button" variant="ghost" onClick={() => setContactOpen(false)}>Vazgeç</Button><Button type="submit" loading={contactSaving}>{editingContact ? "Kaydet" : "Rehbere ekle"}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
